import type { HALLink, HALTemplate } from './api-schemas.ts'

export type TemplateKey = 'create' | 'replace' | 'delete'

/** An operation the caller may perform, with its target resolved. */
export type AvailableTemplate = { method: string; target: string }

/**
 * What a resource says about one operation. `not-adopted` is a resource that sends no `_templates` at
 * all, which says nothing about the operation — unlike `unavailable`, where the resource lists its
 * operations and this one is not among them.
 */
export type TemplateLookup =
  | { state: 'loading' }
  | { state: 'not-adopted' }
  | { state: 'unavailable' }
  | ({ state: 'available' } & AvailableTemplate)

type TemplateCarrier = { _links: { self: HALLink }; _templates?: Partial<Record<TemplateKey, HALTemplate>> }

/**
 * The one way a screen reads an operation off a resource. A `resource` still loading is `undefined`.
 *
 * A template without `target` is sent to the carrying resource's `self`: the API omits `target` only
 * when the two are the same.
 */
export const getTemplate = (resource: TemplateCarrier | undefined, key: TemplateKey): TemplateLookup => {
  if (resource === undefined) {
    return { state: 'loading' }
  }
  if (resource._templates === undefined) {
    return { state: 'not-adopted' }
  }
  const template = resource._templates[key]
  if (template === undefined) {
    return { state: 'unavailable' }
  }
  return { state: 'available', method: template.method, target: template.target ?? resource._links.self.href }
}
